import React, { useEffect, useState } from "react";
import { NEXAR_PC_USER_ID } from "../../adapters/nexar-api/actual-api/consts";
import { useDI } from "../../core/di";

export interface MyNexarContextType {
  user?: User;
  userAuthenticated: boolean | null;
  validateOtpCodeAndLogin(
    countryCode: string,
    phoneNumber: string,
    digits: string,
    clientId: string
  ): Promise<void>;
  clearLoggedInUser: () => void;
  subscriptions: Subscription;
  setSubscriptionError: (message: string, docUrl: string) => void;
  setSubscriptionSuccessMessage: (message: string) => void;
  clearSubscriptionMessage: () => void;
}

const defaultValue: MyNexarContextType = {
  subscriptions: {
    status: "",
  },
  userAuthenticated: false,
  clearLoggedInUser: () => {},
  setSubscriptionError: () => {},
  setSubscriptionSuccessMessage: () => {},
  clearSubscriptionMessage: () => {},
  validateOtpCodeAndLogin: async () => {},
};

interface User {
  firstName: string;
  lastName: string;
}

type SubscriptionError = {
  status: "error";
  errorMessage: string;
  errorDocUrl?: string;
};
type SubscriptionSuccess = {
  status: "success";
  successMessage: string;
};
type SubscriptionUnknown = {
  status: "";
};

type Subscription =
  | SubscriptionError
  | SubscriptionSuccess
  | SubscriptionUnknown;

export const MyNexarContext = React.createContext(defaultValue);

export function MyNexarContextProvider(props: any) {
  const [userAuthenticated, setUserAuthenticated] = useState<boolean | null>(
    null
  );
  const { analytics, nexarApi, logger } = useDI();
  const [user, setUser] = useState<User>();
  const [subscriptions, setSubscriptions] = useState<Subscription>({
    status: "",
  });

  useEffect(() => {
    // On page load (when the context is mounted) check if it's authenticated
    // This also converst from rxdaemon credentials to the new api credentials
    let running = true;
    nexarApi
      .isAuthenticated()
      .then(async (isAuthenticated) => {
        if (running) {
          setUserAuthenticated(isAuthenticated);
          if (isAuthenticated) {
            await identify();
          } else {
            clearLoggedInUser();
          }
        }
      })
      .catch((e) => {
        logger?.Error(e);
        clearLoggedInUser();
      });
    return () => {
      running = false;
    };
  }, []);

  async function identify() {
    const personalCloudUserId = nexarApi.getPersonalCloudUserId();
    analytics?.identify(personalCloudUserId);
    logger?.identify(personalCloudUserId);

    setUserAuthenticated(true);

    await fetchProfile();
  }
  async function fetchProfile() {
    return nexarApi.getUserData().then((userData) => {
      setUser({
        firstName: userData.first_name,
        lastName: userData.last_name,
      });
    });
  }

  async function validateOtpCodeAndLogin(
    countryCode: string,
    phoneNumber: string,
    digits: string,
    clientId: string
  ) {
    await nexarApi.validateOtpCodeAndLogin(
      countryCode,
      phoneNumber,
      digits,
      clientId
    );

    await identify();
  }

  const clearLoggedInUser = () => {
    analytics?.reset();
    setUser(undefined);
    setUserAuthenticated(false);
  };

  const setSubscriptionError = (message: string, docUrl: string) => {
    setSubscriptions({
      status: "error",
      errorMessage: message,
      errorDocUrl: docUrl,
    });
  };
  const clearSubscriptionMessage = () => {
    setSubscriptions({
      status: "",
    });
  };
  const setSubscriptionSuccessMessage = (message: string) => {
    setSubscriptions({ status: "success", successMessage: message });
  };

  const value = {
    user,
    userAuthenticated,
    setUserAuthenticated,
    clearLoggedInUser,
    subscriptions,
    setSubscriptionError,
    setSubscriptionSuccessMessage,
    clearSubscriptionMessage,
    validateOtpCodeAndLogin,
  };

  return (
    <MyNexarContext.Provider value={value}>
      {props.children}
    </MyNexarContext.Provider>
  );
}

export default MyNexarContext;
